import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { usePlayer } from '../../context/PlayerContext';
import { 
  User, LogOut, Crown, Lock, Share2, Video, BookOpen, 
  Edit2, Save, X, Upload, Check, ExternalLink, Copy, Trash2,
  Sun, Dumbbell, Apple
} from 'lucide-react';
import ProgramViewer, { PremiumLockedCard } from '../../components/ProgramViewer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlayerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { player, logout, updatePlayer } = usePlayer();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState(null);
  const [editData, setEditData] = useState({});
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoTitle, setVideoTitle] = useState('');
  const [pendingVideoUrl, setPendingVideoUrl] = useState(null);
  const [deletingVideo, setDeletingVideo] = useState(null);
  const [summerProgramStatus, setSummerProgramStatus] = useState(null);
  const [showSummerProgramModal, setShowSummerProgramModal] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [trainingPrograms, setTrainingPrograms] = useState([]);
  const [nutritionPrograms, setNutritionPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState({ training: null, nutrition: null });
  const [programsLoading, setProgramsLoading] = useState(false);

  useEffect(() => {
    if (!player) {
      navigate('/player/login');
      return;
    }

    // Check for payment status from URL
    if (searchParams.get('payment') === 'success') {
      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        checkPaymentStatus(sessionId);
      }
    } else if (searchParams.get('payment') === 'cancelled') {
      setPaymentMessage({ type: 'info', text: 'Payment was cancelled. You can try again anytime.' });
    }
    
    // Check for Summer Program payment status
    if (searchParams.get('summer_payment') === 'success') {
      const sessionId = searchParams.get('summer_session_id');
      if (sessionId) {
        checkSummerPaymentStatus(sessionId);
      }
    } else if (searchParams.get('summer_payment') === 'cancelled') {
      setPaymentMessage({ type: 'info', text: 'Summer Program payment was cancelled. You can try again anytime.' });
    }

    fetchProfile();
    fetchPlans();
    fetchSummerProgramStatus();
    fetchPrograms();
  }, [player, navigate, searchParams]);

  const fetchPrograms = async () => {
    if (!player?.account_id) return;
    try {
      setProgramsLoading(true);
      const [tRes, nRes] = await Promise.all([
        axios.get(`${API}/player/programs?account_id=${player.account_id}&category=training`).catch(() => ({ data: [] })),
        axios.get(`${API}/player/programs?account_id=${player.account_id}&category=nutrition`).catch(() => ({ data: [] })),
      ]);
      setTrainingPrograms(tRes.data || []);
      setNutritionPrograms(nRes.data || []);
      setSelectedProgramId({
        training: (tRes.data && tRes.data[0]?.id) || null,
        nutrition: (nRes.data && nRes.data[0]?.id) || null,
      });
    } catch (e) {
      // 403 for non-premium is expected — silently skip
    } finally {
      setProgramsLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/player/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setPaymentMessage({ type: 'success', text: 'Payment successful! Your premium features are now active.' });
        // Refresh player data
        const accountRes = await axios.get(`${API}/player/account/${player.account_id}`);
        updatePlayer({
          is_premium: accountRes.data.is_premium,
          subscription_status: accountRes.data.subscription_status,
          subscription_plan: accountRes.data.subscription_plan
        });
        fetchProfile();
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const checkSummerPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/summer-program/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'completed' || response.data.enrolled) {
        setPaymentMessage({ type: 'success', text: 'Welcome to the Summer Program! Your premium features are now active.' });
        // Refresh player data
        const accountRes = await axios.get(`${API}/player/account/${player.account_id}`);
        updatePlayer({
          is_premium: accountRes.data.is_premium,
          subscription_status: accountRes.data.subscription_status,
          subscription_plan: accountRes.data.subscription_plan
        });
        fetchProfile();
        fetchSummerProgramStatus();
      }
    } catch (error) {
      console.error('Error checking summer payment status:', error);
    }
  };

  const fetchSummerProgramStatus = async () => {
    try {
      const response = await axios.get(`${API}/summer-program/status/${player.account_id}`);
      setSummerProgramStatus(response.data);
    } catch (error) {
      console.error('Error fetching summer program status:', error);
    }
  };

  const handleJoinSummerProgram = () => {
    // Open Stripe Payment Link directly
    window.open('https://buy.stripe.com/eVq14madY29X0en6J94ZG02', '_blank');
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/player/profile/${player.account_id}`);
      setProfile(response.data);
      setEditData(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/player/subscription-plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('academic_')) {
      const field = name.replace('academic_', '');
      setEditData(prev => ({
        ...prev,
        academic_info: { ...(prev.academic_info || {}), [field]: value }
      }));
    } else {
      setEditData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/player/profile/${player.account_id}`, editData);
      setProfile(response.data);
      setEditing(false);
      setPaymentMessage({ type: 'success', text: 'Profile saved successfully!' });
      setTimeout(() => setPaymentMessage(null), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setPaymentMessage({ type: 'error', text: 'Error saving profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      const response = await axios.post(
        `${API}/player/checkout?plan=${planId}&account_id=${player.account_id}`
      );
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      setPaymentMessage({ type: 'error', text: 'Error starting checkout. Please try again.' });
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!profile.is_premium) {
      setPaymentMessage({ type: 'error', text: 'Video uploads require a premium subscription.' });
      return;
    }

    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadResponse = await axios.post(`${API}/upload-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Store the URL and prompt for title
      setPendingVideoUrl(uploadResponse.data.url);
      setVideoTitle('');
      setPaymentMessage({ type: 'info', text: 'Video uploaded! Please enter a title below.' });
    } catch (error) {
      console.error('Error uploading video:', error);
      setPaymentMessage({ type: 'error', text: error.response?.data?.detail || 'Error uploading video.' });
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleSaveVideoWithTitle = async () => {
    if (!pendingVideoUrl || !videoTitle.trim()) {
      setPaymentMessage({ type: 'error', text: 'Please enter a title for your video.' });
      return;
    }

    try {
      await axios.post(`${API}/player/profile/${player.account_id}/video`, {
        url: pendingVideoUrl,
        title: videoTitle.trim()
      });

      fetchProfile();
      setPaymentMessage({ type: 'success', text: 'Video saved successfully!' });
      setPendingVideoUrl(null);
      setVideoTitle('');
    } catch (error) {
      console.error('Error saving video:', error);
      setPaymentMessage({ type: 'error', text: error.response?.data?.detail || 'Error saving video.' });
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    setDeletingVideo(videoId);
    try {
      await axios.delete(`${API}/player/profile/${player.account_id}/video/${videoId}`);
      fetchProfile();
      setPaymentMessage({ type: 'success', text: 'Video deleted successfully!' });
    } catch (error) {
      console.error('Error deleting video:', error);
      setPaymentMessage({ type: 'error', text: error.response?.data?.detail || 'Error deleting video.' });
    } finally {
      setDeletingVideo(null);
    }
  };

  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}/profile/${profile.share_slug}`;
    navigator.clipboard.writeText(shareUrl);
    setPaymentMessage({ type: 'success', text: 'Profile link copied to clipboard!' });
    setTimeout(() => setPaymentMessage(null), 3000);
  };

  if (loading) {
    return (
      <div className="page-content">
        <section className="page-hero" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p>Loading your dashboard...</p>
        </section>
      </div>
    );
  }

  const isPremium = profile?.is_premium || player?.is_premium;
  const isSummerProgramMember = summerProgramStatus?.enrolled || player?.subscription_plan === 'summer_program';

  return (
    <div className="page-content">
      {/* Header */}
      <section style={{ 
        padding: '24px var(--spacing-medium)', 
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'var(--brand-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <User size={24} style={{ color: 'black' }} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{player?.name}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                {isSummerProgramMember ? (
                  <span style={{
                    padding: '4px 12px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'black',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Sun size={12} /> SUMMER PROGRAM
                  </span>
                ) : isPremium ? (
                  <span style={{
                    padding: '4px 12px',
                    background: 'var(--brand-primary)',
                    color: 'black',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Crown size={12} /> PREMIUM
                  </span>
                ) : (
                  <span style={{
                    padding: '4px 12px',
                    background: 'rgba(100,100,100,0.3)',
                    color: 'var(--text-light)',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    FREE TIER
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={handleLogout} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LogOut size={18} /> Sign Out
          </button>
        </div>
      </section>

      {/* Payment Message */}
      {paymentMessage && (
        <div style={{
          padding: '16px var(--spacing-medium)',
          background: paymentMessage.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 
                      paymentMessage.type === 'error' ? 'rgba(220, 38, 38, 0.2)' : 'rgba(59, 130, 246, 0.2)',
          borderBottom: `1px solid ${paymentMessage.type === 'success' ? '#22c55e' : 
                         paymentMessage.type === 'error' ? '#dc2626' : '#3b82f6'}`
        }}>
          <div className="container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Check size={18} style={{ color: paymentMessage.type === 'success' ? '#22c55e' : 
                                      paymentMessage.type === 'error' ? '#dc2626' : '#3b82f6' }} />
            {paymentMessage.text}
          </div>
        </div>
      )}

      {/* Main Content */}
      <section style={{ padding: 'var(--spacing-xl) var(--spacing-medium)' }}>
        <div className="container">
          {/* Summer Program Banner - Show to non-members */}
          {!isSummerProgramMember && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.1) 100%)',
              border: '2px solid #f59e0b',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px'
            }} data-testid="summer-program-banner">
              <div>
                <h2 style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '8px', color: '#f59e0b' }}>
                  <Sun size={24} />
                  Pacific Ghost Summer Program
                </h2>
                <p style={{ margin: 0, color: 'var(--text-light)' }}>
                  Full training access, nutrition plans, strength & speed programs, plus premium profile features - all for $1,500
                </p>
              </div>
              <button 
                onClick={() => setShowSummerProgramModal(true)} 
                style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'black',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                data-testid="join-summer-program-btn"
              >
                <Sun size={18} /> Join Summer Program
              </button>
            </div>
          )}

          {/* Upgrade Banner for Free Users (only if not showing summer banner) */}
          {!isPremium && !isSummerProgramMember && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(212, 175, 55, 0.05) 100%)',
              border: '1px solid var(--brand-primary)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '32px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px'
            }}>
              <div>
                <h2 style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Crown size={24} style={{ color: 'var(--brand-primary)' }} />
                  Upgrade to Premium
                </h2>
                <p style={{ margin: 0, color: 'var(--text-light)' }}>
                  Unlock video uploads, academic info, and shareable profile link
                </p>
              </div>
              <button onClick={() => setShowUpgradeModal(true)} className="btn-primary">
                View Plans
              </button>
            </div>
          )}

          {/* Tab Navigation — visible to all premium members (incl. Summer Program) */}
          {isPremium && (
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '24px',
              borderBottom: '1px solid var(--border-medium)',
              paddingBottom: '16px',
              flexWrap: 'wrap'
            }}>
              {[
                { id: 'profile', label: 'Profile', icon: User },
                { id: 'strength', label: 'Training', icon: Dumbbell },
                { id: 'nutrition', label: 'Nutrition', icon: Apple },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  style={{
                    padding: '12px 20px',
                    background: activeTab === tab.id ? 'var(--brand-primary)' : 'rgba(255,255,255,0.1)',
                    color: activeTab === tab.id ? 'black' : 'var(--text-light)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: activeTab === tab.id ? 700 : 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s'
                  }}
                >
                  <tab.icon size={18} />
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Nutrition Tab Content */}
          {isPremium && activeTab === 'nutrition' && (
            <ProgramViewer
              title="Nutrition Plans"
              accentColor="#22c55e"
              icon={Apple}
              programs={nutritionPrograms}
              selectedId={selectedProgramId.nutrition}
              onSelect={(id) => setSelectedProgramId(prev => ({ ...prev, nutrition: id }))}
              loading={programsLoading}
              emptyMessage="No nutrition programs published yet. Your coaches will upload them soon."
            />
          )}

          {/* Strength & Speed (Training) Tab Content */}
          {isPremium && activeTab === 'strength' && (
            <ProgramViewer
              title="Strength & Speed Training"
              accentColor="#ef4444"
              icon={Dumbbell}
              programs={trainingPrograms}
              selectedId={selectedProgramId.training}
              onSelect={(id) => setSelectedProgramId(prev => ({ ...prev, training: id }))}
              loading={programsLoading}
              emptyMessage="No training programs published yet. Your coaches will upload them soon."
            />
          )}

          {/* Profile Tab Content (default) */}
          {(!isPremium || activeTab === 'profile') && (
          <div className="network-card" style={{ padding: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 className="heading-2">Your Profile</h2>
              <div style={{ display: 'flex', gap: '12px' }}>
                {isPremium && profile?.is_public && (
                  <button onClick={copyShareLink} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Share2 size={18} /> Share Profile
                  </button>
                )}
                {editing ? (
                  <>
                    <button onClick={() => setEditing(false)} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <X size={18} /> Cancel
                    </button>
                    <button onClick={handleSaveProfile} className="btn-primary" disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Save size={18} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                  </>
                ) : (
                  <button onClick={() => setEditing(true)} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Edit2 size={18} /> Edit Profile
                  </button>
                )}
              </div>
            </div>

            {/* Basic Info - Always Available */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
              <div className="form-group">
                <label>Name</label>
                {editing ? (
                  <input name="name" value={editData.name || ''} onChange={handleEditChange} data-testid="profile-name-input" />
                ) : (
                  <p style={{ margin: 0 }}>{profile?.name || '-'}</p>
                )}
              </div>
              <div className="form-group">
                <label>Date of Birth</label>
                {editing ? (
                  <input 
                    type="date" 
                    name="date_of_birth" 
                    value={editData.date_of_birth || ''} 
                    onChange={handleEditChange} 
                    data-testid="profile-dob-input"
                    style={{ width: '100%' }}
                  />
                ) : (
                  <p style={{ margin: 0 }}>{profile?.date_of_birth ? new Date(profile.date_of_birth).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '-'}</p>
                )}
              </div>
              <div className="form-group">
                <label>Grad Year / Team</label>
                {editing ? (
                  <select 
                    name="grad_year" 
                    value={editData.grad_year || ''} 
                    onChange={handleEditChange}
                    style={{ width: '100%' }}
                    data-testid="profile-grad-year-select"
                  >
                    <option value="">Select grad year or team</option>
                    <optgroup label="Youth Teams">
                      <option value="10u">10U</option>
                      <option value="11u">11U</option>
                      <option value="12u">12U</option>
                      <option value="13u">13U</option>
                      <option value="14u">14U</option>
                    </optgroup>
                    <optgroup label="High School (Graduation Year)">
                      <option value="2030">2030</option>
                      <option value="2029">2029</option>
                      <option value="2028">2028</option>
                      <option value="2027">2027</option>
                      <option value="2026">2026</option>
                    </optgroup>
                  </select>
                ) : (
                  <p style={{ margin: 0, fontWeight: 600, color: 'var(--brand-primary)' }}>{profile?.grad_year?.toUpperCase() || '-'}</p>
                )}
              </div>
              <div className="form-group">
                <label>High School</label>
                {editing ? (
                  <input name="school" value={editData.school || ''} onChange={handleEditChange} placeholder="Enter high school name" data-testid="profile-school-input" />
                ) : (
                  <p style={{ margin: 0 }}>{profile?.school || '-'}</p>
                )}
              </div>
              <div className="form-group">
                <label>Position</label>
                {editing ? (
                  <input name="position" value={editData.position || ''} onChange={handleEditChange} placeholder="e.g., RHP/SS" data-testid="profile-position-input" />
                ) : (
                  <p style={{ margin: 0 }}>{profile?.position || '-'}</p>
                )}
              </div>
              <div className="form-group">
                <label>Height</label>
                {editing ? (
                  <input name="height" value={editData.height || ''} onChange={handleEditChange} placeholder="e.g., 6'1&quot;" data-testid="profile-height-input" />
                ) : (
                  <p style={{ margin: 0 }}>{profile?.height || '-'}</p>
                )}
              </div>
              <div className="form-group">
                <label>Weight</label>
                {editing ? (
                  <input name="weight" value={editData.weight || ''} onChange={handleEditChange} placeholder="e.g., 185 lbs" data-testid="profile-weight-input" />
                ) : (
                  <p style={{ margin: 0 }}>{profile?.weight || '-'}</p>
                )}
              </div>
            </div>

            {/* Performance Stats - Always Available */}
            <h3 style={{ marginBottom: '16px' }}>Performance Stats</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '32px' }}>
              {[
                { key: 'sixty_yard_time', label: '60 Yard Time' },
                { key: 'home_to_first', label: 'Home to 1st' },
                { key: 'velo', label: 'Velo (mph)' },
                { key: 'exit_velo', label: 'Exit Velo' },
                { key: 'broad_jump', label: 'Broad Jump' },
                { key: 'vertical_jump', label: 'Vertical Jump' }
              ].map(stat => (
                <div key={stat.key} className="form-group">
                  <label>{stat.label}</label>
                  {editing ? (
                    <input name={stat.key} value={editData[stat.key] || ''} onChange={handleEditChange} />
                  ) : (
                    <p style={{ margin: 0, fontWeight: 600, color: 'var(--brand-primary)' }}>{profile?.[stat.key] || '-'}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Premium Features */}
            <div style={{ 
              padding: '24px', 
              background: isPremium ? 'rgba(212, 175, 55, 0.1)' : 'rgba(100, 100, 100, 0.1)', 
              borderRadius: '12px',
              border: `1px solid ${isPremium ? 'var(--brand-primary)' : 'var(--border-medium)'}`,
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                {isPremium ? <Crown size={20} style={{ color: 'var(--brand-primary)' }} /> : <Lock size={20} />}
                <h3 style={{ margin: 0 }}>Premium Features</h3>
                {!isPremium && <span style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>(Upgrade to unlock)</span>}
              </div>

              {isPremium ? (
                <>
                  {/* Academic Info */}
                  <h4 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <BookOpen size={18} /> Academic Information
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                    {[
                      { key: 'gpa', label: 'GPA' },
                      { key: 'sat_score', label: 'SAT Score' },
                      { key: 'act_score', label: 'ACT Score' },
                      { key: 'graduation_year', label: 'Graduation Year' },
                      { key: 'high_school', label: 'High School' },
                      { key: 'intended_major', label: 'Intended Major' }
                    ].map(field => (
                      <div key={field.key} className="form-group">
                        <label>{field.label}</label>
                        {editing ? (
                          <input 
                            name={`academic_${field.key}`} 
                            value={editData.academic_info?.[field.key] || ''} 
                            onChange={handleEditChange} 
                          />
                        ) : (
                          <p style={{ margin: 0 }}>{profile?.academic_info?.[field.key] || '-'}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Bio */}
                  <div className="form-group" style={{ marginBottom: '24px' }}>
                    <label>Bio</label>
                    {editing ? (
                      <textarea 
                        name="bio" 
                        value={editData.bio || ''} 
                        onChange={handleEditChange}
                        rows={4}
                        placeholder="Tell coaches about yourself..."
                      />
                    ) : (
                      <p style={{ margin: 0 }}>{profile?.bio || 'No bio added yet.'}</p>
                    )}
                  </div>

                  {/* Game Videos */}
                  <h4 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Video size={18} /> Game Videos
                  </h4>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 24px',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px dashed var(--border-medium)',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}>
                      <Upload size={18} />
                      {uploadingVideo ? 'Uploading...' : 'Upload Video'}
                      <input type="file" accept="video/*" onChange={handleVideoUpload} style={{ display: 'none' }} disabled={uploadingVideo} />
                    </label>
                  </div>

                  {/* Pending Video Title Input */}
                  {pendingVideoUrl && (
                    <div style={{ 
                      marginBottom: '16px', 
                      padding: '16px', 
                      background: 'rgba(212, 175, 55, 0.1)', 
                      border: '1px solid var(--brand-primary)',
                      borderRadius: '8px' 
                    }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                        Enter Video Title
                      </label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          value={videoTitle}
                          onChange={(e) => setVideoTitle(e.target.value)}
                          placeholder="e.g., Game Highlights vs Rivals 2024"
                          style={{ flex: 1 }}
                        />
                        <button 
                          onClick={handleSaveVideoWithTitle} 
                          className="btn-primary"
                          style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <Check size={16} /> Save
                        </button>
                        <button 
                          onClick={() => { setPendingVideoUrl(null); setVideoTitle(''); }}
                          className="btn-secondary"
                          style={{ padding: '8px' }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <video 
                        src={pendingVideoUrl} 
                        controls 
                        style={{ width: '100%', maxWidth: '300px', marginTop: '12px', borderRadius: '8px' }}
                      />
                    </div>
                  )}

                  {profile?.game_videos?.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                      {profile.game_videos.map((video, idx) => {
                        // Handle both old format (string URL) and new format (object with title/url)
                        const videoUrl = typeof video === 'string' ? video : video.url;
                        const videoTitle = typeof video === 'string' ? `Video ${idx + 1}` : video.title;
                        const videoId = typeof video === 'string' ? null : video.id;
                        
                        return (
                          <div key={videoId || idx} style={{ 
                            background: 'rgba(0,0,0,0.2)', 
                            borderRadius: '8px', 
                            overflow: 'hidden' 
                          }}>
                            <video src={videoUrl} controls style={{ width: '100%', display: 'block' }} />
                            <div style={{ padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>{videoTitle}</span>
                              {videoId && (
                                <button
                                  onClick={() => handleDeleteVideo(videoId)}
                                  disabled={deletingVideo === videoId}
                                  className="btn-secondary"
                                  style={{ 
                                    padding: '6px 10px', 
                                    color: '#ef4444', 
                                    borderColor: '#ef4444',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  <Trash2 size={14} />
                                  {deletingVideo === videoId ? 'Deleting...' : 'Delete'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ color: 'var(--text-light)' }}>No videos uploaded yet.</p>
                  )}

                  {/* Share Link */}
                  {profile?.is_public && (
                    <div style={{ marginTop: '24px', padding: '16px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                      <label style={{ fontSize: '0.875rem', marginBottom: '8px', display: 'block' }}>Your Shareable Profile Link</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input 
                          value={`${window.location.origin}/profile/${profile.share_slug}`} 
                          readOnly 
                          style={{ flex: 1 }}
                        />
                        <button onClick={copyShareLink} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Copy size={16} /> Copy
                        </button>
                        <a 
                          href={`/profile/${profile.share_slug}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn-secondary"
                          style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <ExternalLink size={16} /> View
                        </a>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '24px' }}>
                  <p style={{ color: 'var(--text-light)', marginBottom: '16px' }}>
                    Upgrade to Premium to unlock video uploads, academic info, bio, and shareable profile.
                  </p>
                  <button onClick={() => setShowUpgradeModal(true)} className="btn-primary">
                    Upgrade Now
                  </button>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </section>

      {/* Summer Program Modal */}
      {showSummerProgramModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '650px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            border: '2px solid #f59e0b'
          }}>
            <button
              onClick={() => setShowSummerProgramModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#999'
              }}
              data-testid="close-summer-modal"
            >
              <X size={24} />
            </button>

            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Sun size={56} style={{ color: '#f59e0b', marginBottom: '16px' }} />
              <h2 style={{ fontSize: '1.75rem', color: '#ffffff', margin: 0 }}>Pacific Ghost Summer Program</h2>
              <p style={{ color: '#cccccc', marginTop: '8px' }}>
                Comprehensive baseball training and development
              </p>
            </div>

            <div style={{ 
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.1) 100%)',
              border: '1px solid rgba(245, 158, 11, 0.5)',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <div style={{ fontSize: '3rem', fontWeight: 700, color: '#f59e0b', textAlign: 'center', marginBottom: '8px' }}>
                $1,500
              </div>
              <p style={{ textAlign: 'center', color: '#cccccc', margin: 0 }}>
                Complete Summer Program
              </p>
            </div>

            <h3 style={{ marginBottom: '16px', color: '#f59e0b', fontSize: '1.1rem' }}>What's Included:</h3>
            <ul style={{ listStyle: 'none', padding: 0, marginBottom: '24px' }}>
              {[
                { text: 'Sunday Games (Local, Professionally Coached)', value: '$300/mo $1200 Total - INCLUDED' },
                { text: 'College Showcase Events (Private, Invite Only)', value: '' },
                { text: 'Premium Locker Access (PAC Ghost Website)', value: '$39/mo $180 - INCLUDED' },
                { text: 'Premium Tournament Schedule (WWBA, Fall Classic)', value: 'Tournament fees NOT INCLUDED' },
                { text: 'Monthly Metric Tracking', value: '$150/mo $600 Total - INCLUDED' },
                { text: 'Speed and Strength Program', value: '$150/mo $600 Total - INCLUDED' },
                { text: 'Monthly Grad Year Zoom Call (College Coaches & Advisors)', value: 'INCLUDED' },
                { text: 'Phase 1 College Prep Course', value: '$500 - INCLUDED' },
                { text: 'PG Showcase (PG Scouts & Rankings)', value: '$400 - INCLUDED' }
              ].map((feature, idx) => (
                <li key={idx} style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  padding: '10px 0',
                  borderBottom: idx < 8 ? '1px solid #333' : 'none'
                }}>
                  <Check size={18} style={{ color: '#22c55e', flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <span style={{ color: '#e5e5e5' }}>{feature.text}</span>
                    {feature.value && <span style={{ color: '#f59e0b', fontSize: '0.85rem', marginLeft: '8px' }}>{feature.value}</span>}
                  </div>
                </li>
              ))}
            </ul>
            <div style={{ 
              marginBottom: '24px',
              padding: '14px', 
              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0.1) 100%)',
              border: '1px solid rgba(34, 197, 94, 0.4)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <span style={{ color: '#22c55e', fontWeight: 700, fontSize: '1.1rem' }}>$3,480 VALUE</span>
              <span style={{ color: '#e5e5e5', margin: '0 10px' }}>–</span>
              <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: '1.1rem' }}>Complete Summer Program Only $1,500</span>
            </div>

            <button 
              onClick={handleJoinSummerProgram}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'black',
                border: 'none',
                padding: '16px 32px',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '1.1rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              data-testid="confirm-join-summer-program"
            >
              <Sun size={20} /> Reserve Your Spot - $250
            </button>

            <p style={{ marginTop: '16px', textAlign: 'center', fontSize: '0.875rem', color: '#999' }}>
              Secure payment powered by Stripe
            </p>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowUpgradeModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-light)'
              }}
            >
              <X size={24} />
            </button>

            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <Crown size={48} style={{ color: 'var(--brand-primary)', marginBottom: '16px' }} />
              <h2 className="heading-1">Choose Your Plan</h2>
              <p style={{ color: 'var(--text-light)' }}>Unlock all premium features</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
              {plans.map(plan => (
                <div 
                  key={plan.id} 
                  style={{
                    padding: '24px',
                    background: plan.id === 'annual' ? 'rgba(212, 175, 55, 0.1)' : 'rgba(0,0,0,0.2)',
                    border: `2px solid ${plan.id === 'annual' ? 'var(--brand-primary)' : 'var(--border-medium)'}`,
                    borderRadius: '12px',
                    textAlign: 'center',
                    position: 'relative'
                  }}
                >
                  {plan.id === 'annual' && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      padding: '4px 12px',
                      background: 'var(--brand-primary)',
                      color: 'black',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      fontWeight: 700
                    }}>
                      BEST VALUE
                    </div>
                  )}
                  <h3 style={{ marginBottom: '8px' }}>{plan.name}</h3>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--brand-primary)', marginBottom: '8px' }}>
                    ${plan.price}
                  </div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '16px' }}>
                    {plan.description}
                  </p>
                  <button 
                    onClick={() => handleUpgrade(plan.id)} 
                    className={plan.id === 'annual' ? 'btn-primary' : 'btn-secondary'}
                    style={{ width: '100%' }}
                  >
                    Select
                  </button>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                Secure payment powered by Stripe. Cancel anytime for subscriptions.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerDashboard;
